import React from "react";
import type { ForwardedRef } from "react";

import cn from "classnames";

import { boxForwardRef } from "../../../../../helpers/common/Box/Box";
import { TextCore } from "../../../../../helpers/common/TextCore/TextCore";
import { TEXT_MIXIN_BY_CONTENT_SIZE_MAP } from "../../constants/sizes";
import { createDisplayNameForContentElement } from "../../utils/getDisplayName";
import { createTestIdForContentElement } from "../../utils/getTestId";

import type { BoxComponentWithDefault } from "../../../../../helpers/common/Box/Box";
import type {
    OmitOfUnion,
    Optional,
    PropsWithBaseNever,
} from "../../../../../types/react";
import type { TextProps } from "../../../../Text";
import type {
    ContentAlignmentY,
    ContentSize,
    ContentTextAndIconView,
} from "../../types";

import classes from "./ContentText.styl";

export interface IContentText {
    /** Отвечает за выравнивание контейнера по вертикали */
    selfAlignmentY?: ContentAlignmentY;
    /** Можно переопределить size от Content, для прокидывания своего миксина задайте значение custom */
    size?: ContentSize | "custom";
    ref?: ForwardedRef<HTMLDivElement>;
    className?: string;
}

type ContentTextAllProps = Optional<
    PropsWithBaseNever<IContentText, ContentTextAndIconView>,
    "data-tsid"
> &
    Omit<TextProps, "typographyStyle" | "lineHeight">;

type ConstantContentTextProps = "selfAlignmentY" | "className";

export type ContentTextProps = OmitOfUnion<
    ContentTextAllProps,
    ConstantContentTextProps
>;

export const TEXT_TEST_ID = createTestIdForContentElement("text");

export const ContentText = boxForwardRef<
    BoxComponentWithDefault<"span", ContentTextProps, "className">
>((props, ref) => {
    const {
        children,
        view = "primary",
        customColorsStyles,
        customVarsStyles,
        size = "15",
        maxLines = 2,
        selfAlignmentY = "center",
        className,
        "data-tsid": tsid,
        ...restProps
    } = props as ContentTextAllProps;

    const textClassName = cn(
        classes["text"],
        classes[`__view-${view}`],
        classes[`__self-alignment-y-${selfAlignmentY}`],
        className,
    );

    return (
        <TextCore
            {...restProps}
            textContainerClassName={textClassName}
            typographyStyle={
                (size && TEXT_MIXIN_BY_CONTENT_SIZE_MAP[size]) || "caption-m"
            }
            maxLines={maxLines as any}
            customVarsStyles={cn(customColorsStyles, customVarsStyles)}
            data-tsid={tsid || TEXT_TEST_ID}
            ref={ref}
        >
            {children}
        </TextCore>
    );
});

ContentText.displayName = createDisplayNameForContentElement("Text");

export default ContentText;
